import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { User } from "../../models/User";
import { Diary } from "../../models/Diary";
import { DiaryInstance } from "../../models/DiaryInstance";
import { Activity } from "../../models/Activity";
import { Invitation } from "../../models/Invitation";
import { Instance } from "../../models/Instance";
import { Lesson } from "../../models/Lesson";
import { Answersheet } from "../../models/Answersheet";
import { Participant, Thread } from "../../models/Thread";
import { Message } from "../../models/Message";
import { ElementModel } from "../../models/elements/ElementModel";
import { BlockModel } from "../../models/elements/BlockModel";
import { LineModel } from "../../models/elements/LineModel";
import { PageModel } from "../../models/elements/PageModel";
import { SpaceModel } from "../../models/elements/SpaceModel";
import { TextModel } from "../../models/elements/TextModel";
import { HeadlineModel } from "../../models/elements/HeadlineModel";
import { MediaModel } from "../../models/elements/MediaModel";
import { QuestionYesNoModel } from "../../models/elements/QuestionYesNoModel";
import { QuestionSingleChoiceModel } from "../../models/elements/QuestionSingleChoiceModel";
import { QuestionMultipleChoiceModel } from "../../models/elements/QuestionMultipleChoiceModel";
import { QuestionSliderModel } from "../../models/elements/QuestionSliderModel";
import { QuestionTableModel } from "../../models/elements/QuestionTableModel";
import { QuestionTextStringModel } from "../../models/elements/QuestionTextStringModel";
import { QuestionTextAreaModel } from "../../models/elements/QuestionTextAreaModel";
import { QuestionDateModel } from "../../models/elements/QuestionDateModel";
import { Announcement } from "../../models/Announcement";
import { AlertService } from "../alert/alert.service";
import { HelperService } from "../helper/helper.service";
import { LocalStorageService } from "../local-storage/local-storage.service";
import {
  BlockType,
  DayOfWeek,
  MediaType,
  NotificationInterval,
  TextType,
} from "../../../app/models/elements/Enums";
import { BACKEND_URL } from "../../../environments/environment";
import { Intervention, UnlockType } from "../../models/Intervention";
import { Skill } from "src/app/models/Skill";
import { Schedule } from "src/app/models/Schedule";

@Injectable({
  providedIn: "root",
})
export class ParserService {
  public activityLog: Activity[] = [];
  public invitations: Invitation[] = [];
  public newMessages: number = 0;
  public showConversations: boolean = false;
  public announcements: Announcement[] = [];
  public showAnnouncements: boolean = false;
  public selectedAnnouncement: number = 0;
  public showDiaries: boolean = false;
  public showProgress: boolean = false;

  constructor(
    private alertService: AlertService,
    private localStorage: LocalStorageService,
    private helperService: HelperService,
    private translationService: TranslateService,
  ) { }

  public parseUser(userJSON: object): User {
    let user = userJSON["body"].data;
    if (!user.attributes.settings) {
      user.attributes.settings = {};
    }
    if (!user.attributes.settings.background) {
      user.attributes.settings.background = "0";
    }
    if (!user.attributes.settings.theme) {
      user.attributes.settings.theme = "0";
    }
    if (!user.attributes.settings.language) {
      user.attributes.settings.language = "de";
    }
    if (!user.attributes.settings.locale) {
      user.attributes.settings.locale = "de";
    }
    return new User(
      user.id,
      user.attributes.name,
      user.attributes.firstname,
      user.attributes.lastname,
      user.attributes.settings,
    );
  }

  public parseActivities(activityArrJSON: object): Activity[] {
    const activityArr = activityArrJSON["body"].data;
    const result: Activity[] = [];
    for (const index in activityArr) {
      const attributesJSON = activityArr[index].attributes;
      let activityText = attributesJSON.text;
      if (attributesJSON.name != null) {
        activityText = activityText.replace("{{name}}", attributesJSON.name);
      }
      result.push(
        new Activity(
          activityArr[index].id,
          activityText,
          attributesJSON.action,
          new Date(attributesJSON.created_at),
        ),
      );
    }
    this.activityLog = result;
    return result;
  }

  public parseInvitations(invitationArrJSON: object): Invitation[] {
    const invitations = invitationArrJSON["body"].data;
    const result: Invitation[] = [];
    const now = Date.now();
    for (const index in invitations) {
      const invitation = invitations[index];
      const expiresAt = parseInt(invitation.attributes.expires_at + "000", 10);
      
      if (!this.invitationIsNotOutdated(expiresAt, now)) continue;
      
      const invitationToken = invitation.attributes.uri.replace(
        BACKEND_URL + "/api/v1/studies/invitation?token=",
        "",
      );
      
      const studyTitle = invitation.attributes.study_title;
      const studyDescription = invitation.attributes.study_description;
      const studyPicture = invitation.attributes.study_picture;
      
      let studyImageUrl: string | undefined = undefined;
      if (studyPicture) {
        studyImageUrl = `${BACKEND_URL}/${studyPicture}`;
      }
      
      result.push(new Invitation(
        invitation.id,
        expiresAt,
        invitationToken,
        studyTitle,
        studyDescription,
        studyImageUrl
      ));
    }
    this.invitations = result;
    return result;
  }

  /**
   * Invitations that expired more than 30 days ago are considered outdated and are not considered
   */
  private invitationIsNotOutdated(expiresAt: number, now: number): boolean {
    // daysToMs = 24 * 60 * 60 * 1000 = 86_400_000
    // limitInDays = 30
    // limitInMs = limitInDays * daysToMs = 2_592_000_000
    return (now - expiresAt) < 2592000000;
  }

  private parseUnlockConditional(attributes: any): {
    unlockMultiple: boolean;
    unlockType: UnlockType;
  } {
    const unlockMultipleConditionals = attributes.unlock_multiple_conditionals;
    const jitais = attributes.jitais;
    let unlockMultiple = true;
    let unlockType: UnlockType = "manual";
    if (unlockMultipleConditionals != undefined) {
      unlockMultiple = !!unlockMultipleConditionals;
      if (jitais?.unlock_conditionals_type) {
        unlockType = jitais.unlock_conditionals_type;
      }
    } else if (jitais != undefined) {
      if (jitais.unlock_multiple_conditionals != undefined) {
        unlockMultiple = !!jitais.unlock_multiple_conditionals;
      }
      if (jitais.unlock_conditionals_type) {
        unlockType = jitais.unlock_conditionals_type;
      }
    }
    return { unlockMultiple, unlockType };
  }

  public parseInterventions(interventionArrJSON: object): Intervention[] {
    const interventionsArr = interventionArrJSON["body"].data;
    const result = [];
    for (const index in interventionsArr) {
      result.push(this.parseIntervention(interventionsArr[index]));
    }
    return result;
  }

  public parseIntervention(interventionJSON: object): Intervention {
    let attributes = interventionJSON["attributes"];
    let id = interventionJSON["id"];
    let unlockMultiple = false;
    let unlockType: UnlockType = "manual";
    if (interventionJSON["body"]) {
      attributes = interventionJSON["body"].data.attributes;
      id = interventionJSON["body"].data.id;
      const unlockConditional = this.parseUnlockConditional(attributes);
      unlockMultiple = unlockConditional.unlockMultiple;
      unlockType = unlockConditional.unlockType;
    }
    return new Intervention(
      id,
      attributes.title,
      attributes.description,
      attributes.type,
      attributes.gamification,
      attributes.small_layout,
      unlockMultiple,
      unlockType,
    );
  }

  private progressNeeded(
    instances: Instance[],
    interventions: Intervention[],
  ): boolean {
    if (interventions.length === 0) {
      return false;
    }
    for (const instance of instances) {
      if (
        instance.progress.current_state !== "completed" &&
        instance.progress.current_state !== "canceled"
      ) {
        const intervention = interventions.find(
          (i) => i.id == instance.intervention_id,
        );
        if (
          intervention !== undefined &&
          intervention.gamification &&
          intervention.gamification.skills_order &&
          intervention.gamification.skills_order.length
        ) {
          return true;
        }
      }
    }
    return false;
  }

  public parseInstances(
    instanceArrJSON: object,
    interventions?: Intervention[],
  ): Instance[] {
    const instanceArr = instanceArrJSON["body"].data;
    const result: Instance[] = [];
    for (const index in instanceArr) {
      const instance = this.parseInstance(instanceArr[index]);
      if (!this.showConversations && instance.intervention_type === 'GUIDED') {
        this.showConversations = true;
      }
      result.push(instance);
    }
    //check if user has instances with skills
    if (interventions !== undefined) {
      this.showProgress = this.progressNeeded(result, interventions);
    }
    return result;
  }

  public parseInstance(instanceJSON: object): Instance {
    let attributes = instanceJSON["attributes"];
    let id = instanceJSON["id"];
    let unlockMultiple = false;
    let unlockType: UnlockType = "manual";
    if (instanceJSON["body"]) {
      attributes = instanceJSON["body"].data.attributes;
      id = instanceJSON["body"].data.id;
      const unlockConditional = this.parseUnlockConditional(attributes);
      unlockMultiple = unlockConditional.unlockMultiple;
      unlockType = unlockConditional.unlockType;
    }
    return new Instance(
      id,
      attributes.intervention_id,
      attributes.intervention_type,
      attributes.ecoach_id,
      attributes.patient_id,
      attributes.configuration,
      attributes.progress,
      new Date(attributes.created_at),
      attributes.intervention_title,
      attributes.intervention_description,
      attributes.intervention_media,
      attributes.study_id,
      unlockMultiple,
      unlockType,
    );
  }

  public parseLessons(lessonJSON: object): Lesson[] {
    const lessonArr = lessonJSON["body"].data;
    const result: Lesson[] = [];
    for (const index in lessonArr) {
      result.push(this.parseLesson(lessonArr[index]));
    }
    return result;
  }

  public parseLesson(lessonJSON: object): Lesson {
    let attributes = lessonJSON["attributes"];
    let id = lessonJSON["id"];
    if (lessonJSON["body"]) {
      attributes = lessonJSON["body"].data.attributes;
      id = lessonJSON["body"].data.id;
    }
    let defaultColor = attributes.page_color;
    if (!defaultColor) {
      defaultColor = "#FEFCF0";
    } else if (!defaultColor.startsWith("#")) {
      defaultColor = this.helperService.getPicture(
        defaultColor,
        attributes.study_id,
      );
    }
    const unlock_diaries = attributes.unlock_diaries ?? [];
    return new Lesson(
      parseInt(id, 10),
      attributes.title,
      attributes.description,
      attributes.is_filled_out,
      defaultColor,
      unlock_diaries,
      attributes.questions_required == 1,
      attributes.diary_id,
      attributes.intervention_id,
      attributes.study_id,
      attributes.skills,
    );
  }

  public parseDiaryInstances(
    diaryInstanceArrJSON: object,
    isAllInstances: boolean,
  ): DiaryInstance[] {
    let diaryInstanceArr = diaryInstanceArrJSON["body"].data;
    let result = [];
    for (let index in diaryInstanceArr) {
      result.push(this.parseDiaryInstance(diaryInstanceArr[index]));
    }
    //showDiaries if all instances are requested or if requested for a specific intervention instance, which now has diary instances (result.length > 0)
    if (isAllInstances || result.length > 0) {
      this.showDiaries = result.length > 0;
    }
    return result;
  }

  public parseDiaryInstance(diaryInstanceJSON: object): DiaryInstance {
    const attributes = diaryInstanceJSON["attributes"];
    const schedules: Schedule[] = [];

    for (const scheduleJSON of diaryInstanceJSON["diary_instance_schedules"]) {
      const start: Date | undefined = scheduleJSON.schedule.start === undefined ? undefined : new Date(scheduleJSON.schedule.start);
      const end: Date | undefined = scheduleJSON.schedule.end === undefined ? undefined : new Date(scheduleJSON.schedule.end);

      schedules.push(new Schedule(
        scheduleJSON.id,
        NotificationInterval.parseString(scheduleJSON.schedule.type), // schedule.type corresponds to notification interval
        scheduleJSON.schedule.time_of_day,
        scheduleJSON.schedule.reminders_after_minutes,
        DayOfWeek.parseString(scheduleJSON.schedule.day_of_week),
        scheduleJSON.schedule.day_of_month,
        scheduleJSON.expire_in_seconds,
        start,
        end
      ));
    }

    return new DiaryInstance(
      diaryInstanceJSON["id"],
      attributes.diary_id,
      attributes.diary_title,
      attributes.diary_description,
      attributes.diary_picture,
      attributes.diary_questionnaire_id,
      attributes.study_id,
      attributes.intervention_instance_id,
      schedules,
      attributes.sensor_data,
    );
  }

  public parseDiary(diaryJSON: object): Diary {
    const diary = diaryJSON["body"].data;
    return new Diary(
      diary.id,
      diary.attributes.title,
      diary.attributes.description,
      diary.attributes.picture,
      diary.attributes.study_id,
      diary.attributes.questionnaire_id,
      diary.attributes.page_color,
      diary.attributes.sensor_data,
      diary.attributes.small_layout,
    );
  }

  public parseAnswersheets(answersheetArrJSON: object): Answersheet[] {
    const answersheetArr = answersheetArrJSON["body"].data;
    const result: Answersheet[] = [];
    for (const index in answersheetArr) {
      result.push(this.parseAnswersheet(answersheetArr[index]));
    }
    return result;
  }

  public parseAnswersheet(answersheetJSON: object): Answersheet {
    let attributes = answersheetJSON["attributes"];
    let id = answersheetJSON["id"];
    if (answersheetJSON["body"]) {
      attributes = answersheetJSON["body"].data.attributes;
      id = answersheetJSON["body"].data.id;
    }

    return new Answersheet(
      id,
      attributes.questionnaire_id,
      attributes.intervention_instance_id,
      attributes.diary_instance_id,
      attributes.answers,
      attributes.locale,
      <number>attributes.collected_at * 1000,
      <number>attributes.created_at,
      <number>attributes.updated_at,
      attributes.version,
      attributes.finished,
    );
  }

  /**
   * @param elementsJson (String), contains all Elements
   * @param returns (JSON Object), all question elements as json with element properties at key position;
   * needs keys
   * - questionType (String) (for TextDate questions the specific type from values array has to be used here)
   *`- values (Array) (for Single/Multiple Choice, can be empty otherwise)
   * - translations (JSON Object with language as key, Array with answer strings as value) (answer translations, for Single/Multiple Choice, can be empty otherwise)
   *
   * example: {"2": {"questionType": "TextDate", "values": [], translations: {}}, "5": {"questionType": "SingleChoice", "values": [a, b],
   * translations: {"en": ["answer a", "answer b"], "de": ["Antwort a", "Antwort b"]}}}
   */
  parseLabelList(elementJSON: object): any {
    let parsedResponse = elementJSON["body"].data.attributes.elements;
    if (parsedResponse.length == 0) {
      return [];
    }

    let language = this.localStorage.getAppLanguage();
    let result = {};
    for (let entry of parsedResponse) {
      if (entry.elementtype != "elements/questions") continue;
      let type = entry["questiontype"];

      if (type == "TextDate") {
        type =
          "Text" +
          entry.values[0].replace("date", "Date").replace("time", "Time");
      }

      result[entry.position] = {
        questionType: type,
        values: entry.values,
        translations: {
          [language]: entry.answers,
        },
      };
    }
    return result;
  }

  /**
   * @method parseElements
   * @param elementJSON
   * @return array of PageComponents. Each PageComponent has an array containing ElementModels (e.g. QuestionModels and their QuestionType)
   *
   * todoNewElementFormat maybe check for questions, conditional blocks etc in skills
   */
  public parseElements(elementJSON: object): PageModel[] {
    let parsedResponse = elementJSON["body"].data.attributes.elements;
    if (parsedResponse.length == 0) {
      return [new PageModel(0, "", "", "")];
    }

    // iterate through all elements of the response and try to parse them
    let elements: Array<PageModel | BlockModel> = [];
    // the first element always has to be a page
    // old lessons don't have this
    if (parsedResponse[0].elementtype != "elements/pages") {
      elements.push(new PageModel(0, "", "", ""));
    }

    for (let entry of parsedResponse) {
      let parsedElement = null;
      switch (entry.elementtype) {
        case "elements/elements":
          break;
        case "elements/headlines":
          parsedElement = this.parseHeadline(entry);
          break;
        case "elements/texts":
          parsedElement = this.parseText(entry);
          break;
        case "elements/media":
          parsedElement = this.parseMedia(entry);
          break;
        case "elements/questions":
          parsedElement = this.parseQuestion(entry);
          break;
        case "elements/pages":
          parsedElement = this.parsePage(entry);
          elements.push(parsedElement);
          break;
        case "elements/lines":
          parsedElement = new LineModel(entry.position);
          break;
        case "elements/spaces":
          parsedElement = this.parseSpace(entry);
          break;
        case "elements/blockopens":
          parsedElement = this.parseBlock(entry);
          elements.push(parsedElement);
          break;
        case "elements/blockcloses":
          // find corresponding block opens element
          let filtered = elements.filter((p) => p instanceof BlockModel);
          let block = filtered[filtered.length - 1];
          let spliced = elements.splice(
            elements.lastIndexOf(block),
            elements.length - 1,
          );
          parsedElement = spliced[0];
          // in case of conditional pages append them to the corresponding block
          // if (spliced.length > 1) parsedElement.elements.concat(spliced.slice(1))
          break;
        default:
          throw "switch default!";
      }
      if (parsedElement && entry.elementtype != "elements/blockcloses") {
        parsedElement.position = entry.position;
      }
      if (
        parsedElement &&
        entry.elementtype != "elements/blockopens" &&
        entry.elementtype != "elements/pages"
      ) {
        elements[elements.length - 1].elements.push(parsedElement);
      }
    }

    return elements as PageModel[];
  }

  /**
   * @method parseHeadline
   * @param content
   * @return QuestionModel for a simple headline with no actual question and its tiype
   *
   * parse response part into Headline QuestionModel
   */
  public parseHeadline(content: string): HeadlineModel {
    let headline =
      content["headline"] == null ? "No headline" : content["headline"];
    return new HeadlineModel(null, headline, content["color"]);
  }

  /**
   * @method parseText
   * @param content
   * @return QuestionModels for a simple text with no actual question and its type.
   */
  public parseText(content: string): TextModel {
    let parsedType: TextType;
    switch (content["type"]) {
      case "none":
        parsedType = TextType.NONE;
        break;
      case "important":
        parsedType = TextType.IMPORTANT;
        break;
      case "tip":
        parsedType = TextType.TIP;
        break;
      case "info":
        parsedType = TextType.INFO;
        break;
      case "success":
        parsedType = TextType.SUCCESS;
        break;
      case "highlight":
        parsedType = TextType.HIGHLIGHT;
        break;
      case "table":
        parsedType = TextType.TABLE;
        break;
      default:
        parsedType = TextType.NONE;
    }
    let text = content["text"];
    let parser = new DOMParser();
    let htmlDoc = parser.parseFromString(text, "text/html");
    let imgs = htmlDoc.getElementsByTagName("img");
    for (let i = 0; i < imgs.length; i++) {
      let img = imgs[i];
      if (img.width > 0) {
        img.style.setProperty(
          "width",
          img.width.toString() + "px",
          "important",
        );
      }
    }
    // let textSanitized = this.sanitizer.bypassSecurityTrustHtml(text);
    return new TextModel(null, text, parsedType);
  }

  /**
   * @method parseMedia
   * @param content
   * @return Media
   */
  parseMedia(content: Record<string, any>): MediaModel {
    // set type for media model
    let isAudio = false;
    let type: MediaType;
    switch (content["mimetype"]) {
      case "image/png":
        type = MediaType.IMAGE_PNG;
        break;
      case "image/jpeg":
        type = MediaType.IMAGE_JPEG;
        break;
      case "image/gif":
        type = MediaType.IMAGE_GIF;
        break;
      case "image/svg+xml":
        type = MediaType.IMAGE_SVG;
        break;
      case "audio/mpeg":
        type = MediaType.AUDIO_MPEG;
        break;
      case "audio/wav":
      case "audio/x-wav":
        type = MediaType.AUDIO_WAV;
        break;
      case "audio/mp4":
      case "audio/x-m4a":
        type = MediaType.AUDIO_M4A;
        break;
      case "video/mp4":
        // try catch block for backward compatibility
        // old interventions miss the is_audio key in video/mp4 elements
        try {
          isAudio = content["is_audio"] == 1;
        } catch (e) { }
        type = MediaType.VIDEO_MP4;
        break;
      case "application/msword":
        type = MediaType.APPLICATION_DOC;
        break;
      case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        type = MediaType.APPLICATION_DOCX;
        break;
      case "application/vnd.ms-powerpoint":
        type = MediaType.APPLICATION_PPT;
        break;
      case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
        type = MediaType.APPLICATION_PPTX;
        break;
      case "application/vnd.oasis.opendocument.text":
        type = MediaType.APPLICATION_ODT;
        break;
      case "application/pdf":
        type = MediaType.APPLICATION_PDF;
        break;
      case "text/vtt":
        type = MediaType.WEB_VTT;
        break;
      default:
        type = MediaType.NOT_SUPPORTED;
    }

    const uri: string = content["uri"] == null ? "Empty" : content["uri"];

    // get name of media model as accessed at the back-end
    // let currentIndexOfChar = 0;
    // let indexOfLastChar = 0;
    // while (currentIndexOfChar != -1) {
    //   indexOfLastChar = currentIndexOfChar;
    //   currentIndexOfChar = uri.indexOf('/', currentIndexOfChar + 1);
    // }
    // let file = uri.substr(indexOfLastChar + 1);

    // set data for the model
    // let platformIsAndroid = this.platform.is('android');

    return new MediaModel(
      null,
      type,
      isAudio,
      uri,
      content["description"],
      content["subtitle"],
      content["filename"],
      content["video_subtitle_uri"],
      content["video_subtitle_original_name"],
      content["width"],
      content["banner"] == 1,
    );
  }

  /**
   * @method parseQuestion
   * @param content
   * @return QuestionModel and its Type
   *
   * Parse part of response into corresponding Questiontype
   */
  public parseQuestion(content: string): ElementModel {
    let questiontype: string = content["questiontype"];
    let questionModel = null;
    switch (questiontype) {
      case "YesNoSwitch":
        questionModel = this.parseYesNoQuestion(content);
        break;
      case "SingleChoice":
        questionModel = this.parseSingleChoice(content);
        break;
      case "MultipleChoice":
        questionModel = this.parseMultipleChoice(content);
        break;
      case "Slider":
        questionModel = this.parseSlider(content);
        break;
      case "QuestionTable":
        questionModel = this.parseQuestionTable(content);
        break;
      case "TextDate":
        questionModel = this.parseTextDate(content);
        break;
      case "TextString":
        questionModel = this.parseTextString(content);
        break;
      case "TextArea":
        questionModel = this.parseTextArea(content);
        break;
      default:
        return null;
    }

    return questionModel == null ? null : questionModel;
  }

  /**
   * @method parseYesNoQuestion
   * @param content
   * @return QuestionYesNo
   *
   * parse response part into QuestionYesNoModel
   */
  private parseYesNoQuestion(content: string): QuestionYesNoModel {
    return new QuestionYesNoModel(
      null,
      content["question"],
      content["required"] == 1,
      content["label"],
    );
  }

  /**
   * @method parseSingleChoice
   * @param content
   * @return parseSingleChoiceModel
   *
   * parse response part into QuestionSingleChoiceModel
   */
  private parseSingleChoice(content: string): QuestionSingleChoiceModel {
    const options: string[] = [];
    if (content["answers"] != null) {
      for (const answer of content["answers"]) {
        options.push(answer);
      }
    }

    return new QuestionSingleChoiceModel(
      null,
      content["question"],
      content["required"] == 1,
      content["label"],
      content["answers"],
      content["values"],
    );
  }

  /**
   * @method parseMultipleChoice
   * @param content
   * @return parseMultipleChoiceModel
   *
   * parse response part into QuestionMultipleChoiceModel
   */
  private parseMultipleChoice(content: string): QuestionMultipleChoiceModel {
    const options: string[] = [];
    if (content["answers"] != null) {
      for (const answer of content["answers"]) {
        options.push(answer);
      }
    }
    return new QuestionMultipleChoiceModel(
      null,
      content["question"],
      content["required"] == 1,
      content["label"],
      content["answers"],
      content["values"],
    );
  }

  /**
   * @method parseSlider
   * @param content
   * @return parseSliderModel
   *
   * parse response part into QuestionSliderModel
   */
  private parseSlider(content: string): QuestionSliderModel {
    let leftLabel =
      content["answers"] != null
        ? content["answers"][0]["label"]
        : "No left label";
    let rightLabel =
      content["answers"] != null
        ? content["answers"][1]["label"]
        : "No right label";

    return new QuestionSliderModel(
      null,
      content["question"],
      content["required"] == 1,
      content["label"],
      leftLabel,
      rightLabel,
      content["values"]["min"],
      content["values"]["max"],
      content["values"]["step"],
      content["values"]["start"],
    );
  }

  /**
   * @method parseQuestionTable
   * @param content
   * @return parseQuestionTableModel
   *
   * parse response part into QuestionTableModel
   */
  private parseQuestionTable(content: string): QuestionTableModel {
    return new QuestionTableModel(
      null,
      content["question"],
      content["required"] == 1,
      content["label"],
      content["answers"],
      content["values"]["min"],
      content["values"]["max"],
    );
  }

  /**
   * @method parseTextDate
   * @param content
   * @return parseTextDateModel
   *
   * parse response part into QuestionTextDateModel
   */
  private parseTextDate(content: string): QuestionDateModel {
    let type = content["values"] != null ? content["values"][0] : "date";
    return new QuestionDateModel(
      null,
      content["question"],
      content["required"] == 1,
      content["label"],
      type,
    );
  }

  /**
   * @method parseTextString
   * @param content
   * @return parseTextStringModel
   *
   * parse response part into QuestionTextStringModel
   */
  private parseTextString(content: string): QuestionTextStringModel {
    return new QuestionTextStringModel(
      null,
      content["question"],
      content["required"] == 1,
      content["label"],
    );
  }

  /**
   * @method parseTextArea
   * @param content
   * @return parseTextAreaModel
   *
   * parse response part into QuestionTextAreaModel
   */
  private parseTextArea(content: string): QuestionTextAreaModel {
    return new QuestionTextAreaModel(
      null,
      content["question"],
      content["required"] == 1,
      content["label"],
    );
  }

  private parsePage(content: any): PageModel {
    let color: string;
    try {
      color = content["color"];
    } catch {
      color = "";
    }

    let condition: string;
    try {
      condition = content["condition"] === null ? "" : content["condition"];
    } catch {
      condition = "";
    }

    let progressbar_text: string | null;
    try {
      progressbar_text =
        content["progressbar_text"] === "" ? null : content["progressbar_text"];
    } catch {
      progressbar_text = null;
    }

    return new PageModel(null, color, condition, progressbar_text);
  }

  private parseSpace(content: any): SpaceModel {
    let size: number;
    try {
      size = parseInt(content["attributes"]["elements"]["size"]);
    } catch {
      size = 1;
    }
    return new SpaceModel(null, size);
  }

  private parseBlock(content: string): BlockModel {
    let typeJson = content["type"];
    let type: BlockType;
    switch (typeJson) {
      case "none":
        type = BlockType.NONE;
        break;
      case "details":
        type = BlockType.DETAILS;
        break;
      case "conditional":
        type = BlockType.CONDITIONAL;
        break;
      case "both":
        type = BlockType.BOTH;
        break;
    }
    try {
      return new BlockModel(
        null,
        content["text"],
        type,
        content["condition"]["ifBlock"],
        content["condition"]["thenBlock"]["repeat"],
      );
    } catch (e) {
      this.alertService.showError(
        "ERROR.ERROR",
        "ERROR.ERROR_COULD_NOT_LOAD_LESSON_OLD_MODEL",
      );
    }
  }

  public parseThreads(messagesJSON: object, feedbackJSON: object): Thread[] {
    const { messages, feedback } = this.setupThreadsState(messagesJSON, feedbackJSON);
    return messages.concat(feedback);
  }

  public setupThreadsState(messagesJSON: object, feedbackJSON: object) {
    const { threads: messages, newMessages } = this.parseThreadsObject(messagesJSON);
    const { threads: feedback, newMessages: newFeedback } = this.parseThreadsObject(feedbackJSON);
    this.newMessages = newMessages + newFeedback;
    if (messages.length > 0 || feedback.length > 0) {
      this.showConversations = true;
    }
    return { messages, feedback };
  }

  private parseThreadsObject(threadsJSON: object) {
    let newMessages = 0;
    const threadsArr = threadsJSON["body"].data;
    const threads: Thread[] = [];
    for (const i in threadsArr) {
      const threadJSON = threadsArr[i];
      const json = {
        id: threadJSON.id,
        attributes: threadJSON.attributes,
        includedUsers: threadJSON.relationships.participants.data,
        includedMessages: threadJSON.relationships.messages?.data || [],
      };
      const thread = this.parseThread(json);
      threads.push(thread);
      if (!thread.read) {
        newMessages += thread.unreadMessages;
      }
    }
    return { threads, newMessages };
  }

  public parseThread(threadJSON: object): Thread {
    let attributes = threadJSON["attributes"];
    let id = threadJSON["id"];
    let includedUsers = threadJSON["includedUsers"];
    let includedMessages = threadJSON["includedMessages"];
    if (threadJSON["body"]) {
      attributes = threadJSON["body"].data.attributes;
      id = threadJSON["body"].data.id;
      includedUsers = threadJSON["body"].data.relationships.participants.data;
      includedMessages = threadJSON["body"].data.relationships.messages.data;
    }
    const messages = this.getMessages(includedMessages);
    // Set picture of thread to picture of last sender
    const authorId = messages.length > 0 ? messages.at(-1).authorId : 1;
    const picture = this.helperService.getUserPicture(authorId);
    const participants: Participant[] = [];
    for (const participantIndex in includedUsers) {
      const participant = includedUsers[participantIndex];
      participants.push({
        id: participant.id,
        name: this.getAuthorName(participant),
      });
    }
    // For feedback thread: subjectText is name of lesson; For chat thread: subjectText is name of participants
    const subjectText = attributes.answersheet_id
      ? attributes.intervention_instance_progress.questionnaire_name
      : participants.map((p) => p.name).join(", ");
    //when requesting a thread it is set to read & unread messages = 0; if feedback it is set to the correct value from progress below
    const { read, unreadMessages } = this.getMessagesReadData(attributes);
    return new Thread(
      id,
      subjectText,
      attributes.answersheet_id,
      attributes.intervention_instances_id,
      attributes.intervention_instance_progress.questionnaire_id,
      attributes.intervention_instance_progress.questionnaire_feedback_required,
      read,
      attributes.created_at,
      unreadMessages,
      messages,
      participants,
      picture,
      attributes.updated_at,
    );
  }

  private getMessages(includedMessages: any[]): Message[] {
    const messages: Message[] = [];
    for (const messageIndex in includedMessages) {
      const message = includedMessages[messageIndex];
      const messageId = message.id;
      const messageAttributes = message.attributes;
      const authorData = message.relationships.author.data;
      const messageAuthorName = this.getAuthorName(authorData);
      const id = authorData?.id ?? -1;
      messages.push(
        new Message(
          messageId,
          id,
          messageAuthorName,
          messageAttributes.body,
          messageAttributes.created_at,
        ),
      );
    }
    return messages;
  }

  private getAuthorName(authorJSON: object): string {
    if (!authorJSON) {
      return this.translationService.instant("CHAT_FEEDBACK.UNKNOWN");
    }
    if (authorJSON["id"] == this.localStorage.getUserId()) {
      return this.translationService.instant("CHAT_FEEDBACK.ME");
    }
    const attributes = authorJSON["attributes"];
    if (!attributes) {
      return this.translationService.instant("CHAT_FEEDBACK.UNKNOWN");
    }
    if (attributes.firstname && attributes.lastname) {
      return attributes.firstname + " " + attributes.lastname;
    }
    return attributes.name || this.translationService.instant("CHAT_FEEDBACK.UNKNOWN");
  }

  private getMessagesReadData(attributes: any) {
    let read = true;
    let unreadMessages = 0;
    //for feedback thread: subjectText is name of lesson; progress.feedback_required read attribute used for (un)read messages
    if (attributes.answersheet_id) { // Feedback
      const instanceProgress = attributes.intervention_instance_progress;
      if (!instanceProgress.questionnaire_feedback_required) {
        if (attributes.unread) {
          read = !attributes.unread.is_unread;
          unreadMessages = attributes.unread.messages;
        }
      } else if (attributes.unread) {
        unreadMessages = attributes.unread.messages;
        // questionnaire_feedback_read is null if instance was canceled
        if (unreadMessages > 0 || instanceProgress.questionnaire_feedback_read == null) {
          read = !attributes.unread.is_unread;
        } else {
          read = instanceProgress.questionnaire_feedback_read;
        }
      } else {
        // If message count is unavailable, set read to true if instance was canceled or base it on initial feedback
        read = (
          instanceProgress.questionnaire_feedback_read == null ||
          instanceProgress.questionnaire_feedback_read
        );
      }
    } else if (attributes.unread) { // Chat
      read = !attributes.unread.is_unread;
      unreadMessages = attributes.unread.messages;
    }
    return { read, unreadMessages };
  }

  public parseAnnouncements(annArrJSON: object): Announcement[] {
    const annArr = annArrJSON["body"].data;
    const result: Announcement[] = [];
    for (const index in annArr) {
      const attributesJSON = annArr[index].attributes;
      result.push(
        new Announcement(
          annArr[index].id,
          attributesJSON.type,
          attributesJSON.title,
          attributesJSON.message,
        ),
      );
    }
    return result;
  }

  public parseSkills(skillArrJSON: object): Skill[] {
    const skillArr = skillArrJSON["body"].data;
    const result = [];
    for (const index in skillArr) {
      result.push(this.parseSkill(skillArr[index]));
    }
    return result;
  }

  public parseSkill(skillJSON: object): Skill {
    let attributes = skillJSON["attributes"];
    let id = skillJSON["id"];
    if (skillJSON["body"]) {
      attributes = skillJSON["body"].data.attributes;
      id = skillJSON["body"].data.id;
    }
    return new Skill(
      id,
      attributes.intervention_id,
      attributes.title,
      attributes.color,
      attributes.icon,
      null,
      attributes.study_id,
    );
  }
}
